import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from 'components/system/layouts/Layout';
import { Grid, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ScreenContext from 'Contexts/Common/ScreenContext';
import COMMON from 'constants/common';
import redirectLinks from 'constants/redirectLinks';
import ReturnButton from 'components/system/atoms/buttons/ReturnButton';
import DecisionButton from 'components/machineConf/atoms/DecisionButton';
import BorderBottomBox from 'components/system/atoms/boxs/BorderBottomBox';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { initializeGroupSlice } from 'redux/slices/groupSlice';
import { ConfirmDialog, InfoDialog } from 'components/system/organisms';
import MSG from 'constants/messages';
import MsgHelper from 'utils/message.helper';
import { REST_API } from 'constants/apiUrls';
import HttpConnection from 'utils/httpConnection';
import SelectList, { INITIAL_SELECT_LIST } from 'components/support/organisms/supports/SelectList';
import { MachineListType } from 'types/group/GroupType';
import CLSFCN from 'constants/classification';
import OrderSettingTable from '../../components/group/organisms/tables/OrderSettingTable';
import { DataSourceType, SelectListType } from '../../types/support/supportType';

/* ************ Context ************ */
const ScreenContextValue = {
  title: '登録機器連動順設定',
  screenId: COMMON.SCREEN_ID.LED_ORDER_SET,
  currentMenu: COMMON.MENU.GROUP,
};

/* ************ Style ************ */
const useStyles = makeStyles()((theme) => ({
  content: {
    height: '598px',
    width: '1140px',
    padding: '10px 28px 24px 16px',
    position: 'relative',
  },
  topArea: {
    display: 'flex',
    justifyContent: 'flex-start',
    '& .returnButton': {
      padding: '4px 0px 0px 24px',
      width: '224px',
    },
  },
  description: {
    paddingTop: '30px',
    paddingLeft: '20px',
    lineHeight: '28px',
    fontSize: '23px',
    '& .doneImage': {
      width: '56px',
      height: '28px',
      padding: '2px 0px',
      borderRadius: '4px',
      fontSize: '19px',
      textAlign: 'center',
      verticalAlign: 'middle',
      display: 'inline-block',
      background: theme.palette.common.black,
      color: theme.palette.common.white,
    },
  },
  dataTitle: {
    display: 'flex',
  },
  decision: {
    textAlign: 'right',
    margin: '24px 0',
    position: 'absolute',
    right: '28px',
    bottom: '-42px',
  },
  boxTextRight: {
    paddingLeft: 40,
    paddingTop: 12,
    flex: 1,
  },
  machineCount: {
    paddingTop: 12,
    fontSize: 28,
    borderBottom: '2px solid #707070',
    '& .value': {
      marginLeft: 4,
    },
  },
  titleContent: {
    margin: '30px 0 16px 12px',
    fontSize: '23px',
    float: 'left',
  },
  anchorDialog: {
    marginLeft: '250px',
  },
  groupName: {
    fontSize: 28,
    borderBottom: '2px solid #707070',
    '& .value': {
      marginLeft: 4,
    },
  },
  contentText: { fontSize: '28px' },
  pulldownList: {
    '& #selectList-dialog-title': {
      justifyContent: 'center',
    },
    '& #scrollSelectList': {
      textAlign: 'center',
      '& div': {
        padding: 0,
      },
    },
  },
  buttonSize: {
    fontSize: '28px',
  },
}));

const INPUT_NAMES = {
  ORDER_NO: 'order_no',
};

const LedOrderSetting: React.FC<React.PropsWithChildren<unknown>> = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { classes } = useStyles();

  /* ************ Global State ************ */
  const groupInfo = useAppSelector((state) => state.group.ledGroup);

  /* ************ Local State ************ */
  const [selectList, setSelectList] = useState<SelectListType>(INITIAL_SELECT_LIST.selectList);
  const [selectedRow, setSelectedRow] = useState<number | undefined>();
  const [openConfirmDlg, setOpenConfirmDlg] = useState<boolean>(false);
  const [openInfoDialog, setOpenInfoDialog] = useState<boolean>(false);
  const nextScrName = useRef<string>('グループカテゴリ選択');
  const nextScrUrl = useRef<string>(redirectLinks.GROUP_SELECT_CONF);

  const anchorDialogRef = useRef(null);

  /* ************ Local Function ************ */

  // 連動順の選択肢を作成
  const dataSource: DataSourceType[] = (() =>
    groupInfo.machineList.map((m) => ({
      name: m.orderNo.toString(),
      value: m.orderNo.toString(),
    })))();

  /* ************ API ************ */
  // 景品獲得演出グループ情報を登録/更新
  const registGroupInfoApi = async () => {
    const http = new HttpConnection({ dispatch });
    await http.post(REST_API.GROUP.REGISTER_LED_GROUP, {
      groupId: groupInfo.groupId,
      versionNo: groupInfo.versionNo,
      groupName: groupInfo.groupName,
      status: groupInfo.status,
      setting: groupInfo.setting,
      directing: groupInfo.directing,
      ecoMode: groupInfo.ecoMode,
      eco_type: groupInfo.ecoType,
      communicationConf: groupInfo.communicationConf,
      bgm: groupInfo.bgm,
      se: groupInfo.se,
      voice: groupInfo.voice,
      keepEcoType: groupInfo.keepEcoType,
      ledLeft: {
        lLedColor4: groupInfo.ledLeft.ledColor4,
        lLedEffect4: groupInfo.ledLeft.ledEffect4,
        lLedColor3: groupInfo.ledLeft.ledColor3,
        lLedEffect3: groupInfo.ledLeft.ledEffect3,
        lLedColor2: groupInfo.ledLeft.ledColor2,
        lLedEffect2: groupInfo.ledLeft.ledEffect2,
        lLedColor1: groupInfo.ledLeft.ledColor1,
        lLedEffect1: groupInfo.ledLeft.ledEffect1,
      },
      ledRight: {
        rLedColor4: groupInfo.ledRight.ledColor4,
        rLedEffect4: groupInfo.ledRight.ledEffect4,
        rLedColor3: groupInfo.ledRight.ledColor3,
        rLedEffect3: groupInfo.ledRight.ledEffect3,
        rLedColor2: groupInfo.ledRight.ledColor2,
        rLedEffect2: groupInfo.ledRight.ledEffect2,
        rLedColor1: groupInfo.ledRight.ledColor1,
        rLedEffect1: groupInfo.ledRight.ledEffect1,
      },
      machineList: groupInfo.machineList,
      ledLeftGrand: {
        lLedColor4: groupInfo.ledLeftGrand.ledColor4 ?? CLSFCN.LED_GRAND_COLOR.SAMPLE_PATTERN,
        lLedEffect4: groupInfo.ledLeftGrand.ledEffect4 ?? CLSFCN.LED_EFFECT.NONE_LIGHTING,
        lLedColor3: groupInfo.ledLeftGrand.ledColor3 ?? CLSFCN.LED_GRAND_COLOR.SAMPLE_PATTERN,
        lLedEffect3: groupInfo.ledLeftGrand.ledEffect3 ?? CLSFCN.LED_EFFECT.NONE_LIGHTING,
        lLedColor2: groupInfo.ledLeftGrand.ledColor2 ?? CLSFCN.LED_GRAND_COLOR.SAMPLE_PATTERN,
        lLedEffect2: groupInfo.ledLeftGrand.ledEffect2 ?? CLSFCN.LED_EFFECT.NONE_LIGHTING,
        lLedColor1: groupInfo.ledLeftGrand.ledColor1 ?? CLSFCN.LED_GRAND_COLOR.SAMPLE_PATTERN,
        lLedEffect1: groupInfo.ledLeftGrand.ledEffect1 ?? CLSFCN.LED_EFFECT.NONE_LIGHTING,
      },
      ledRightGrand: {
        rLedColor3: groupInfo.ledRightGrand.ledColor3 ?? CLSFCN.LED_GRAND_COLOR.SAMPLE_PATTERN,
        rLedEffect3: groupInfo.ledRightGrand.ledEffect3 ?? CLSFCN.LED_EFFECT.NONE_LIGHTING,
        rLedColor2: groupInfo.ledRightGrand.ledColor2 ?? CLSFCN.LED_GRAND_COLOR.SAMPLE_PATTERN,
        rLedEffect2: groupInfo.ledRightGrand.ledEffect2 ?? CLSFCN.LED_EFFECT.NONE_LIGHTING,
        rLedColor1: groupInfo.ledRightGrand.ledColor1 ?? CLSFCN.LED_GRAND_COLOR.SAMPLE_PATTERN,
        rLedEffect1: groupInfo.ledRightGrand.ledEffect1 ?? CLSFCN.LED_EFFECT.NONE_LIGHTING,
      },
      duoCustomLed: {
        mainR: groupInfo.duoCustomLed.mainR,
        mainG: groupInfo.duoCustomLed.mainG,
        mainB: groupInfo.duoCustomLed.mainB,
        subR: groupInfo.duoCustomLed.subR,
        subG: groupInfo.duoCustomLed.subG,
        subB: groupInfo.duoCustomLed.subB,
      },
      duoLedCustomFlg: groupInfo.duoLedCustomFlg,
      frameWhite: groupInfo.frameWhite,
    });
  };
  /* ************ UseEffect ************ */
  useEffect(() => {
    // 遷移先画面によって独自処理を実施
    if (groupInfo.nextFromOrderSetScr === COMMON.SCREEN_ID.GROUP_SELECT_CATEGORY) {
      nextScrName.current = 'グループカテゴリ選択';
      nextScrUrl.current = redirectLinks.GROUP_SELECT_CONF;
    } else if (groupInfo.nextFromOrderSetScr === COMMON.SCREEN_ID.GROUP_LIST) {
      nextScrName.current = '登録グループ一覧';
      nextScrUrl.current = redirectLinks.GROUP_LIST_DATA;
    } else if (groupInfo.nextFromOrderSetScr === COMMON.SCREEN_ID.MACHINE_DETAIL) {
      nextScrName.current = '機器一覧';
      nextScrUrl.current = redirectLinks.MAINT_MACHINE_LIST;
    }
  }, []);

  /* ************ Events ************ */

  // return button
  const handleClickReturn = () => {
    navigate(redirectLinks.GROUP_LED_SELECT_REGISTER_MACHINE);
  };

  const handleClickRow = useCallback(
    (
      ev: React.MouseEvent<HTMLTableRowElement>,
      orderNo: number,
      rows: MachineListType[],
      handleClickNewOrderNo: (newOrderNo: number, oldOrderNo: number, machineList: MachineListType[]) => void,
    ) => {
      setSelectedRow(orderNo);
      setSelectList({
        open: true,
        anchorEL: anchorDialogRef.current,
        className: classes.pulldownList,
        dataSource,
        name: INPUT_NAMES.ORDER_NO,
        placement: 'right-start',
        title: '連動順を選択してください',
        value: orderNo.toString(),
        onClick: (d) => {
          handleClickNewOrderNo(Number(d.value), orderNo, rows);
        },
        onClose: () => {
          setSelectList(INITIAL_SELECT_LIST.selectList);
          setSelectedRow(undefined);
        },
      });
    },
    [groupInfo.machineList],
  );

  // click on ok button ob the confirm dialog
  const handleOkConfirmDlg = () => {
    registGroupInfoApi().then(() => {
      setOpenConfirmDlg(false);
      setOpenInfoDialog(true);
    });
  };

  // click on ok button ob the infomation dialog
  const handleOkInfoDlg = () => {
    setOpenInfoDialog(false);
    dispatch(initializeGroupSlice());
    navigate(nextScrUrl.current);
  };

  return (
    <ScreenContext.Provider value={ScreenContextValue}>
      <Layout>
        <div className={classes.content}>
          <div className={classes.topArea}>
            <ReturnButton onClick={handleClickReturn}>登録機器選択</ReturnButton>
            <div className={classes.boxTextRight}>
              <BorderBottomBox value={groupInfo.groupName} className={classes.groupName} />
              <BorderBottomBox
                value={`機器数  ${groupInfo.machineList.length.toString()}件`}
                className={classes.machineCount}
              />
            </div>
          </div>
          {groupInfo.communicationConf !== CLSFCN.GROUP_COMMUNICATION_CONF.SYNC ? (
            <div className={classes.description}>
              通信設定が同期の場合は連動順の設定は不要です。
              <br />
              そのまま<Typography className="doneImage">登録</Typography>してください。
            </div>
          ) : (
            <div>
              <div className={classes.dataTitle}>
                <div className={classes.titleContent}>機器を選択して連動順を設定</div>
                <div className={classes.anchorDialog} ref={anchorDialogRef} />
              </div>
              <OrderSettingTable
                groupCategory={COMMON.GROUP_CATEGORY.LED}
                rows={groupInfo.machineList}
                handleClickRow={handleClickRow}
                selectedOrderNo={selectedRow}
              />
            </div>
          )}
          <Grid className={classes.decision}>
            <DecisionButton onClick={() => setOpenConfirmDlg(true)}>
              <span className={classes.buttonSize}>登録</span>
            </DecisionButton>
          </Grid>
        </div>
        <SelectList selectList={selectList} />
        <ConfirmDialog
          open={openConfirmDlg}
          msg={MsgHelper.messageFormat(MSG.INF224, 'LED演出設定')}
          onOk={handleOkConfirmDlg}
          onCancel={() => setOpenConfirmDlg(false)}
          addClass={{
            contentText: classes.contentText,
          }}
        />
        <InfoDialog
          open={openInfoDialog}
          msg={`${MsgHelper.messageFormat(MSG.INF302, 'LED演出設定')}\n${MsgHelper.messageFormat(
            MSG.INF225,
            nextScrName.current,
          )}`}
          closeFunc={() => {
            handleOkInfoDlg();
          }}
        />
      </Layout>
    </ScreenContext.Provider>
  );
};

export default LedOrderSetting;
